import { LIVE_PREVIEW_MOCKED_ID } from 'src/js/modules/blocks/constants/common';
import { blocks } from '@yola/ws-sdk';
import injectGlobals from 'src/js/modules/blocks/helpers/inject-globals';
import compileBlockNode from './compile-block-node';
import replaceOptionalChildren from './replace-optional-children';
import handleBackgroundImage from './handle-background-image';
import getChildrenToUpdate from './get-children-to-update';
import handleBackgroundImageAsFallback from './handle-background-image-as-fallback';
import handleBlockMediaImageAsFallback from './handle-block-media-image-as-fallback';
import shouldUseCachedDestinationBlock from './should-use-cached-destination-block';
import handleChangeOverlay from './handle-change-overlay';
import compileLivePreviewNode from '../block-settings/compile-live-preview-node';
import dropTextResizing from './drop-text-resizing';
import { REGULAR_IMAGE_ATTRIBUTES_FOR_SHALLOW_PROXY } from '../../constants/common';
import {
  WHITESPACE_TOP_ATTRIBUTE,
  WHITESPACE_BOTTOM_ATTRIBUTE,
  BORDER_WIDTH_ATTRIBUTE,
  BORDER_BOTTOM_ATTRIBUTE,
  FULLSCREEN,
} from '../../constants/display-options';
import copyDisplayOptionByAttribute from '../copy-display-option-by-attribute';
import setMatchingSurface from './set-matching-surface';

const preprocessBlockLayout = (
  block,
  {
    sortBlockVariationId,
    selectedLayoutVariationId,
    displayOptions,
    backgroundOption,
    cachedBackgroundOption,
    refId,
    blockNode,
    originalStaticBlockNode,
    originalBlockNode,
    compiledNodesCache,
    selectedPreviewNode,
    originalVariationId,
    optionalChildrenState,
    allowReplacingWithDifferentTags,
  }
) => {
  const { id, html, variationId } = block;
  const markup = injectGlobals(html);
  const cachedDestinationBlockNode = compiledNodesCache.get(variationId);

  let destinationBlockNode;

  const isLivePreview = variationId === LIVE_PREVIEW_MOCKED_ID;
  const isOriginalVariationId = variationId === originalVariationId;

  if (isLivePreview) {
    destinationBlockNode = compileLivePreviewNode({
      optionalChildren: optionalChildrenState,
      sortBlockVariationId,
      selectedLayoutVariationId,
      displayOptions,
      selectedPreviewNode,
      backgroundOption,
    });
  } else {
    const shouldUseCachedBlock = shouldUseCachedDestinationBlock({
      cachedDestinationBlockNode,
      destinationBlockNode,
      displayOptions,
    });

    if (shouldUseCachedBlock) {
      destinationBlockNode = cachedDestinationBlockNode;
    } else {
      destinationBlockNode = compileBlockNode(markup, id, variationId, refId);
      compiledNodesCache.delete(variationId);
    }
  }

  if (!cachedDestinationBlockNode) {
    compiledNodesCache.set(variationId, destinationBlockNode);
  }

  const optionalChildrenToUpdate = getChildrenToUpdate(originalBlockNode, destinationBlockNode);

  const attributesForShallowProxy =
    !isLivePreview && !isOriginalVariationId ? REGULAR_IMAGE_ATTRIBUTES_FOR_SHALLOW_PROXY : [];

  replaceOptionalChildren({
    optionalChildrenToUpdate,
    originalBlockNode: originalStaticBlockNode,
    destinationBlockNode,
    attributesForShallowProxy,
    allowReplacingWithDifferentTags,
  });

  handleChangeOverlay({
    sourceBlockNode: blockNode,
    displayOptions,
    destinationBlockNode,
  });

  [
    WHITESPACE_TOP_ATTRIBUTE,
    WHITESPACE_BOTTOM_ATTRIBUTE,
    BORDER_WIDTH_ATTRIBUTE,
    BORDER_BOTTOM_ATTRIBUTE,
    FULLSCREEN,
  ].forEach((attribute) =>
    copyDisplayOptionByAttribute({
      displayOptions,
      destinationBlockNode,
      attribute,
    })
  );

  setMatchingSurface({ displayOptions, destinationBlockNode });

  const backgroundHandledSuccessfully = handleBackgroundImage({
    destinationBlockNode,
    variationId,
    backgroundOption: backgroundOption || cachedBackgroundOption,
  });

  const originalOptionalChildren = blocks.accessors.getOptionalChildrenData(originalBlockNode);
  const destinationOptionalChildren =
    blocks.accessors.getOptionalChildrenData(destinationBlockNode);

  if (!backgroundHandledSuccessfully) {
    handleBackgroundImageAsFallback({
      originalBlockNode: originalStaticBlockNode,
      destinationBlockNode,
      originalOptionalChildren,
      destinationOptionalChildren,
      backgroundOption,
      displayOptions,
    });
  }

  handleBlockMediaImageAsFallback({
    originalBlockNode: originalStaticBlockNode,
    destinationBlockNode,
    originalOptionalChildren,
    destinationOptionalChildren,
    backgroundOption,
  });

  if (!isLivePreview && !isOriginalVariationId) {
    dropTextResizing(destinationBlockNode);
  }

  return destinationBlockNode.outerHTML;
};

export default preprocessBlockLayout;
